import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { ShoppingBag, Search, ShoppingCart, Plus, Star, Wallet, Info } from 'lucide-react';
import { useAlert } from '../context/AlertContext';
import API_BASE_URL from '../config/api';
import { useNavigate, useLocation } from 'react-router-dom';

const Shop = () => {
    const [packageTabs, setPackageTabs] = useState([]);
    const [activeTab, setActiveTab] = useState(null); // Will be user_package_id
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [totalWallet, setTotalWallet] = useState(0);
    const { showAlert } = useAlert();
    const navigate = useNavigate();
    // eslint-disable-next-line no-unused-vars
    const location = useLocation();

    useEffect(() => {
        fetchProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchProducts = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/shop/products.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setPackageTabs(data.package_tabs || []);
                setTotalWallet(data.total_shopping_wallet || 0);

                // Determine active tab: URL param > Current State > First Available
                if (data.package_tabs?.length > 0) {
                    const params = new URLSearchParams(window.location.search);
                    const urlTab = params.get('user_package_id');
                    const tabExists = urlTab && data.package_tabs.some(t => t.user_package_id.toString() === urlTab);

                    if (tabExists) {
                        setActiveTab(urlTab);
                    } else if (!activeTab) {
                        setActiveTab(data.package_tabs[0].user_package_id.toString());
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            showAlert('Failed to load products', 'error');
        } finally {
            setLoading(false);
        }
    };

    const addToCart = async (product) => {
        if (!activeTab) {
            showAlert('Please select a package tab first', 'error');
            return;
        }
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/shop/cart.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    product_id: product.id,
                    quantity: 1,
                    user_package_id: activeTab
                })
            });
            const data = await response.json();
            if (data.success) {
                showAlert('Added to cart', 'success');
            } else {
                showAlert(data.message, 'error');
            }
        } catch (error) {
            showAlert('Failed to add to cart', 'error');
        }
    };

    const selectedTab = packageTabs.find(tab => tab.user_package_id.toString() === activeTab);
    const displayProducts = selectedTab ? selectedTab.products : [];

    const filteredProducts = displayProducts.filter(p =>
        p.product_name.toLowerCase().includes(searchTerm.toLowerCase())
    );



    return (
        <Layout>
            <div className="p-4 sm:p-6 max-w-7xl mx-auto">
                {/* Header */}
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 lg:mb-8 gap-4 sm:gap-6 bg-white p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50/50 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                    <div className="z-10">
                        <h1 className="text-xl sm:text-2xl font-black text-gray-900 flex items-center gap-2 sm:gap-3">
                            <div className="p-2 bg-blue-50 rounded-lg">
                                <ShoppingBag className="text-blue-600 w-5 h-5 sm:w-6 sm:h-6" />
                            </div>
                            Package Shopping
                        </h1>
                        <p className="text-gray-500 mt-1 font-medium text-[10px] sm:text-xs">Use package balances to select products</p>
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full lg:w-auto z-10">
                        {/* Global Wallet Info */}
                        <div className="bg-gradient-to-br from-indigo-600 to-blue-600 p-3 sm:p-4 rounded-xl sm:rounded-2xl text-white shadow-lg shadow-blue-100 flex items-center gap-3">
                            <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm">
                                <Wallet size={18} className="sm:w-5 sm:h-5" />
                            </div>
                            <div>
                                <p className="text-[8px] sm:text-[10px] uppercase font-bold tracking-widest opacity-80">Shopping Wallet</p>
                                <p className="text-base sm:text-xl font-black">₹{totalWallet.toLocaleString()}</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <div className="relative flex-1 sm:w-48 lg:w-64">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                                <input
                                    type="text"
                                    placeholder="Search products..."
                                    className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border-gray-100 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/10 text-xs sm:text-sm font-medium"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <button
                                onClick={() => navigate('/checkout')}
                                className="p-2.5 bg-gray-900 text-white rounded-xl hover:bg-black transition-all flex items-center gap-2 text-xs sm:text-sm font-bold shrink-0"
                            >
                                <ShoppingCart size={18} />
                                <span className="hidden sm:inline">Checkout</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Tabs for Packages */}
                {packageTabs.length > 0 ? (
                    <>
                        <div className="flex items-center gap-3 mb-6 overflow-x-auto pb-2 no-scrollbar">
                            {packageTabs.map((tab) => (
                                <button
                                    key={tab.user_package_id}
                                    onClick={() => {
                                        const newTab = tab.user_package_id.toString();
                                        setActiveTab(newTab);
                                        // Update URL without reloading
                                        const url = new URL(window.location);
                                        url.searchParams.set('user_package_id', newTab);
                                        window.history.pushState({}, '', url);
                                    }}
                                    className={`px-4 py-3 rounded-xl font-bold transition-all whitespace-nowrap border-2 flex flex-col items-start min-w-[140px] text-sm ${activeTab === tab.user_package_id.toString()
                                        ? 'bg-white border-blue-600 shadow-md shadow-blue-50 text-blue-600 ring-2 ring-blue-50'
                                        : 'bg-white text-gray-400 border-gray-100 hover:border-blue-200'
                                        }`}
                                >
                                    <span className="text-[10px] uppercase tracking-widest mb-0.5 flex items-center justify-between w-full">
                                        {tab.package_name}
                                        {tab.instance_count > 1 && (
                                            <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-[8px] font-black">
                                                {tab.instance_count} PKGS
                                            </span>
                                        )}
                                    </span>
                                    <span className={`text-base ${activeTab === tab.user_package_id.toString() ? 'text-gray-900' : ''}`}>
                                        ₹{parseFloat(tab.remaining_balance).toLocaleString()}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {/* Selected Package Info Alert */}
                        {selectedTab && (
                            <div className="mb-6 p-3 sm:p-4 bg-indigo-50 border border-indigo-100 rounded-xl flex items-start sm:items-center gap-3 text-indigo-700">
                                <Info className="flex-shrink-0 mt-0.5 sm:mt-0" size={18} />
                                <div className="text-xs sm:text-sm">
                                    <span className="font-bold">Info:</span> Using <span className="font-black">"{selectedTab.package_name}"</span> balance.
                                    Available: <span className="font-black tracking-tight">₹{parseFloat(selectedTab.remaining_balance).toLocaleString()}</span>.
                                </div>
                            </div>
                        )}

                        {/* Products Grid */}
                        {loading ? (
                            <div className="flex justify-center p-24">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                            </div>
                        ) : filteredProducts.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                                {filteredProducts.map(product => {
                                    const canAfford = parseFloat(selectedTab?.remaining_balance || 0) >= parseFloat(product.price);
                                    return (
                                        <div key={product.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl hover:shadow-blue-100/30 transition-all duration-300 group flex flex-col h-full">
                                            {/* Image */}
                                            <div className="h-48 sm:h-56 bg-gray-50/50 relative overflow-hidden flex items-center justify-center p-6 group-hover:bg-white transition-colors">
                                                {product.image_url ? (
                                                    <img
                                                        src={product.image_url}
                                                        alt={product.product_name}
                                                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                                                    />
                                                ) : (
                                                    <ShoppingBag size={40} className="text-gray-200" />
                                                )}

                                                <div className="absolute top-3 left-3 space-y-1.5 flex flex-col items-start translate-x-[-2px] translate-y-[-2px]">
                                                    <div className="bg-blue-600 text-white text-[8px] font-black px-1.5 py-0.5 rounded-lg uppercase tracking-wider shadow-lg shadow-blue-500/20 w-fit drop-shadow-sm border border-blue-400/30">
                                                        {selectedTab.package_code}
                                                    </div>
                                                    {parseInt(product.buy_quantity) > 0 && parseInt(product.get_free_quantity) > 0 && (
                                                        <div className="bg-amber-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded-lg uppercase tracking-wider shadow-lg shadow-amber-500/20 w-fit animate-pulse-subtle border border-amber-400/30">
                                                            BUY {product.buy_quantity} GET {product.get_free_quantity} FREE
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Content */}
                                            <div className="p-4 sm:p-5 flex-1 flex flex-col">
                                                <div className="flex-1">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="text-[9px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md border border-blue-100 uppercase tracking-tight">
                                                            {parseFloat(product.pv_points)} PV
                                                        </div>
                                                        <div className="flex items-center gap-1 text-yellow-500">
                                                            <Star size={10} fill="currentColor" />
                                                            <span className="text-[10px] font-bold">4.9</span>
                                                        </div>
                                                    </div>
                                                    <h3 className="font-bold text-gray-900 text-base sm:text-lg mb-1 line-clamp-1 group-hover:text-blue-600 transition-colors tracking-tight">
                                                        {product.product_name}
                                                    </h3>
                                                    <p className="text-gray-400 text-xs leading-relaxed line-clamp-2 mb-4 h-8">
                                                        {product.description || 'Premium quality product selected for your package.'}
                                                    </p>
                                                </div>

                                                <div className="flex items-center justify-between pt-4 border-t border-gray-50 mt-auto gap-2">
                                                    <div className="flex flex-col">
                                                        <span className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Price</span>
                                                        <div className="text-lg sm:text-xl font-black text-gray-900 tracking-tight">
                                                            ₹{parseFloat(product.price).toLocaleString()}
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => addToCart(product)}
                                                        disabled={!canAfford}
                                                        className={`px-4 py-2 rounded-lg transition-all shadow-sm active:scale-95 flex items-center gap-1.5 group/btn text-xs sm:text-sm font-bold ${canAfford
                                                            ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-200'
                                                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                            }`}
                                                        title={canAfford ? 'Add to Package Cart' : 'Insufficient Package Balance'}
                                                    >
                                                        <Plus size={16} className={canAfford ? "group-hover/btn:rotate-90 transition-transform" : ""} />
                                                        Buy
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-20 bg-white rounded-3xl border border-gray-50">
                                <ShoppingBag size={40} className="text-gray-200 mx-auto mb-4" />
                                <h3 className="text-xl font-bold text-gray-900 mb-1">No products</h3>
                                <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">
                                    {searchTerm ? "No matches found" : "Empty Catalog"}
                                </p>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="text-center py-20 bg-white rounded-3xl border border-gray-100">
                        <Plus size={40} className="text-blue-200 mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-gray-900 mb-2">No Active Packages</h3>
                        <p className="text-gray-500 max-w-xs mx-auto text-sm">
                            Purchase a package first to shop.
                        </p>
                        <button
                            onClick={() => navigate('/products/packages')}
                            className="mt-6 px-6 py-3 bg-blue-600 text-white rounded-xl font-bold shadow-lg shadow-blue-100 hover:bg-blue-700 transition-all text-xs uppercase tracking-widest"
                        >
                            View Packages
                        </button>
                    </div>
                )}
            </div>
            <style>{`
                @keyframes pulse-subtle {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.85; transform: scale(1.05); }
                }
                .animate-pulse-subtle {
                    animation: pulse-subtle 2s ease-in-out infinite;
                }
                .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
                .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
            `}</style>
        </Layout>
    );
};

export default Shop;
